/* ===========================================================
   editor/autocorrect.js — fixes as you type (his ask, 2026-10-28;
   rules, no AI)

   After a space, Enter, punctuation, or a half-second pause:
     - a known misspelling with one fix is fixed (teh → the)
     - one that could be several words gets a faint red underline;
       right-click it for the choices (googl → Google / google)
     - "i" → "I", a sentence's first letter capitalised, double
       spaces closed, space before , . ! ? removed
   Ctrl+Z right after a fix puts your word back. Your own spellings
   stay yours once you undo them. proofread() is the beta button.
   =========================================================== */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/* misspelling → fix, or [choices] when it's not clear which word you meant */
const FIX = Object.fromEntries(`teh:the adn:and taht:that thier:their recieve:receive beleive:believe seperate:separate
definately:definitely occured:occurred untill:until wich:which becuase:because becasue:because beacuse:because alot:a_lot
dont:don't doesnt:doesn't didnt:didn't cant:can't wont:won't isnt:isn't wasnt:wasn't arent:aren't werent:weren't couldnt:couldn't
shouldnt:shouldn't wouldnt:wouldn't im:I'm ive:I've id:I'd youre:you're theyre:they're thats:that's whats:what's lets:let's
tommorow:tomorrow tomorow:tomorrow tommorrow:tomorrow goverment:government enviroment:environment wierd:weird freind:friend
begining:beginning calender:calendar comming:coming truely:truly finaly:finally realy:really basicly:basically probaly:probably
probly:probably acheive:achieve arguement:argument embarass:embarrass existance:existence foriegn:foreign grammer:grammar
happend:happened knowlege:knowledge libary:library neccessary:necessary necesary:necessary noticable:noticeable occassion:occasion
peice:piece posession:possession prefered:preferred publically:publicly recomend:recommend refered:referred relevent:relevant
rember:remember remeber:remember responsable:responsible rythm:rhythm sucess:success suprise:surprise tounge:tongue
wether:whether wich:which writting:writing yeild:yield accomodate:accommodate adress:address agian:again aswell:as_well
beutiful:beautiful buisness:business carefull:careful cemetary:cemetery collegue:colleague comittee:committee completly:completely
concious:conscious curiousity:curiosity dissapear:disappear dissapoint:disappoint excercise:exercise experiance:experience
familar:familiar febuary:February goverment:government gaurd:guard hieght:height hygeine:hygiene independant:independent
intresting:interesting jewlery:jewelry judgement:judgment liason:liaison maintainance:maintenance millenium:millennium
mischievious:mischievous misspel:misspell mispell:misspell occurence:occurrence oppurtunity:opportunity paralell:parallel
persue:pursue posible:possible privelege:privilege questionaire:questionnaire reccomend:recommend rediculous:ridiculous
scedule:schedule sentance:sentence speach:speech strenght:strength therfore:therefore threshhold:threshold tomatos:tomatoes
tyranny:tyranny vaccuum:vacuum vegtable:vegetable wendsday:Wednesday wensday:Wednesday thursady:Thursday saturaday:Saturday
youve:you've hes:he's shes:she's wouldve:would've couldve:could've shouldve:should've`.split(/\s+/).filter(Boolean).map(p => p.split(":")).filter(p => p[1]).map(([a, b]) => [a, b.replace(/_/g, ' ')]));
const MAYBE = {
  googl: ['Google', 'google'], googel: ['Google', 'google'], gogle: ['Google', 'google'],
  its: null, their: null, there: null, then: null, than: null, your: null, were: null,   // real words: never touched
  form: null, loose: null, lose: null, affect: null, effect: null,
  wat: ['what', 'wait'], wih: ['with', 'which'], ot: ['to', 'of', 'or'], fo: ['of', 'for'], hte: ['the', 'he'],
  thsi: ['this', 'thus'], tehy: ['they'], waht: ['what'], jsut: ['just'], konw: ['know'], nto: ['not', 'into'],
  ti: ['it', 'to'], si: ['is', 'so'], nad: ['and', 'had'], dose: ['does', 'dose'], ur: ['your', "you're"], u: ['you'],
};

const key = new PluginKey('autocorrect');
const WORD = /([A-Za-z']+)$/;

function keepCase(orig, fix) {
  if (orig === orig.toUpperCase() && orig.length > 1) return fix.toUpperCase();
  if (orig[0] === orig[0].toUpperCase()) return fix[0].toUpperCase() + fix.slice(1);
  return fix;
}

/** Fix the word that ends at `end` in this text block. Returns a transaction or null. */
function fixAt(state, pos, kept) {
  const $p = state.doc.resolve(pos);
  if (!$p.parent.isTextblock || $p.parent.type.spec.code) return null;
  const before = $p.parent.textBetween(0, $p.parentOffset, null, '￼');
  const m = before.match(WORD);
  if (!m) return null;
  const word = m[1], lower = word.toLowerCase();
  const from = pos - word.length;
  let tr = null;
  const fix = FIX[lower];
  if (fix && !kept.has(lower)) {
    tr = state.tr.insertText(keepCase(word, fix), from, pos);
  } else if (lower === 'i') {
    tr = state.tr.insertText('I', from, pos);
  } else {
    // first word of a sentence gets a capital
    const head = before.slice(0, -word.length);
    if (/^[a-z]/.test(word) && (/^\s*$/.test(head) || /[.!?]\s+$/.test(head)) && !/^(e\.g|i\.e|etc)$/.test(lower) && !/https?:|www\./.test(head.slice(-12))) {
      tr = state.tr.insertText(word[0].toUpperCase(), from, from + 1);
    }
  }
  if (tr) tr.setMeta(key, { fixed: { from, word } });
  return tr;
}

/** Faint red under words with several possible fixes. */
function maybes(doc) {
  const decos = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    for (const m of node.text.matchAll(/[A-Za-z']+/g)) {
      const opts = MAYBE[m[0].toLowerCase()];
      if (opts) decos.push(Decoration.inline(pos + m.index, pos + m.index + m[0].length, { class: 'ac-maybe', title: `Did you mean: ${opts.join(', ')}?`, 'data-ac': opts.join('|') }));
    }
  });
  return DecorationSet.create(doc, decos);
}

export const Autocorrect = Extension.create({
  name: 'autocorrect',
  addOptions() { return { enabled: () => true }; },
  addProseMirrorPlugins() {
    const ext = this;
    const kept = new Set();   // words you put back with Ctrl+Z
    let timer = null, last = null;
    return [new Plugin({
      key,
      state: {
        init: (_, s) => ({ maybe: maybes(s.doc), flash: null }),
        apply: (tr, old) => {
          const m = tr.getMeta(key);
          let flash = old.flash && tr.docChanged ? { from: tr.mapping.map(old.flash.from), to: tr.mapping.map(old.flash.to) } : old.flash;
          if (m?.fixed) flash = { from: m.fixed.from, to: tr.mapping.map(m.fixed.from + m.fixed.word.length, 1) };
          if (m?.unflash) flash = null;
          return { maybe: tr.docChanged ? maybes(tr.doc) : old.maybe, flash };
        },
      },
      view: () => ({
        // the faint green on a fixed word fades after a moment
        update(view) {
          const f = key.getState(view.state).flash;
          if (f && !this.t) this.t = setTimeout(() => { this.t = null; if (!view.isDestroyed) view.dispatch(view.state.tr.setMeta(key, { unflash: true })); }, 900);
        },
        destroy() { clearTimeout(this.t); },
      }),
      props: {
        decorations: s => {
          const { maybe, flash } = key.getState(s);
          return flash && flash.to > flash.from ? maybe.add(s.doc, [Decoration.inline(flash.from, flash.to, { class: 'ac-fixed' })]) : maybe;
        },
        handleTextInput(view, from, to, text) {
          if (!ext.options.enabled() || !/^[\s.,!?;:)]$/.test(text)) return false;
          // close double spaces and spaces before punctuation
          const prev = view.state.doc.textBetween(Math.max(0, from - 1), from);
          if (text === ' ' && prev === ' ') return true;
          if (/[.,!?;:]/.test(text) && prev === ' ') { view.dispatch(view.state.tr.insertText(text, from - 1, to)); return true; }
          const tr = fixAt(view.state, from, kept);
          if (tr) { view.dispatch(tr); last = tr.getMeta(key).fixed; }
          return false;
        },
        handleKeyDown(view, e) {
          clearTimeout(timer);
          if (e.key === 'z' && (e.ctrlKey || e.metaKey) && last) { kept.add(last.word.toLowerCase()); last = null; return false; }
          if (e.key === 'Enter' && ext.options.enabled()) {
            const tr = fixAt(view.state, view.state.selection.from, kept);
            if (tr) view.dispatch(tr);
            return false;
          }
          if (e.key.length === 1) {
            timer = setTimeout(() => {
              if (!ext.options.enabled() || view.isDestroyed) return;
              const tr = fixAt(view.state, view.state.selection.from, kept);
              // a pause only fixes clear misspellings, not capitals mid-word
              if (tr && FIX[tr.getMeta(key).fixed.word.toLowerCase()]) view.dispatch(tr);
            }, 500);
          }
          return false;
        },
      },
    })];
  },
});

/** Proofread beta (his account only): a whole-doc pass. Returns how many things it changed. */
export function proofread(editor) {
  let n = 0;
  const { state } = editor;
  const tr = state.tr;
  const edits = [];
  state.doc.descendants((node, pos) => {
    if (!node.isTextblock || node.type.spec.code) return;
    const text = node.textContent;
    const base = pos + 1;
    const rules = [
      [/ {2,}/g, () => ' '],
      [/ +([,.!?;:])/g, (m, p) => p],
      [/\b(\w+) \1\b/gi, (m, w) => w],                                   // "the the"
      [/\bi\b/g, () => 'I'],
      [/\ba ([aeiouAEIOU]\w)/g, (m, w) => `an ${w}`],
      [/\ban ([^aeiouAEIOUh\W]\w)/g, (m, w) => `a ${w}`],
      [/([.!?] )([a-z])/g, (m, a, b) => a + b.toUpperCase()],
      [/^([a-z])/g, (m, a) => a.toUpperCase()],
      [/([,;:])(\w)/g, (m, a, b) => `${a} ${b}`],
      [/\b([A-Za-z']+)\b/g, (m, w) => (FIX[w.toLowerCase()] ? keepCase(w, FIX[w.toLowerCase()]) : w)],
    ];
    for (const [rx, fn] of rules) {
      for (const m of text.matchAll(rx)) {
        const out = fn(...m);
        if (out !== m[0]) edits.push({ from: base + m.index, to: base + m.index + m[0].length, text: out });
      }
    }
    if (/[a-z0-9]$/i.test(text.trim()) && text.trim().split(/\s+/).length > 6 && node.type.name === 'paragraph') {
      edits.push({ from: base + text.trimEnd().length, to: base + text.trimEnd().length, text: '.' });
    }
  });
  // right to left, skipping overlaps, so positions stay true
  edits.sort((a, b) => b.from - a.from);
  let floor = Infinity;
  for (const e of edits) { if (e.to > floor) continue; tr.insertText(e.text, e.from, e.to); floor = e.from; n++; }
  if (n) editor.view.dispatch(tr);
  return n;
}
